import { ChevronDownIcon, CircleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "../../../../../lib/utils";

interface SubsectionHeaderProperties {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  contentId: string;
  hasError?: boolean;
  /** Rendered inside the toggle button, centered between the title and the warning/chevron icons on the right. */
  beforeIcons?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

export function SubsectionHeader({
  title,
  isOpen,
  onToggle,
  contentId,
  hasError,
  beforeIcons,
  trailing,
  className,
}: SubsectionHeaderProperties) {
  const { t } = useTranslation();

  return (
    <div className={cn("bg-subsection-header flex items-center gap-2 rounded-md px-3 py-2", className)}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={onToggle}
        className={cn(
          "flex flex-1 items-center gap-2 text-left text-sm font-medium",
          !beforeIcons && "justify-between"
        )}
      >
        <span className="shrink-0">{title}</span>
        {beforeIcons && (
          <>
            <span aria-hidden="true" className="flex-1" />
            {beforeIcons}
            <span aria-hidden="true" className="flex-1" />
          </>
        )}
        <span className="flex shrink-0 items-center gap-1.5">
          {hasError && (
            <CircleAlertIcon
              className="size-4 shrink-0 text-amber-500"
              fill="currentColor"
              stroke="var(--color-card)"
              aria-label={t("review.hasErrorLabel")}
              role="img"
            />
          )}
          <ChevronDownIcon
            className={cn(
              "text-muted-foreground size-4 shrink-0 transition-transform duration-200",
              isOpen ? "rotate-180" : "rotate-0"
            )}
            aria-hidden="true"
          />
        </span>
      </button>
      {trailing}
    </div>
  );
}
